import i18n from "../../../Localization/i18next";
import Attribute from "../../Firestore/Attribute";

/**
 * ExerciseSearchService - A service for searching exercises with modifiers
 *
 * This service parses search queries to identify main exercises and their modifiers,
 * returning ranked suggestions based on match quality.
 *
 * Features:
 * - Case-insensitive search
 * - Partial matching with word order flexibility
 * - Multiple modifier detection
 * - Localized string comparison (current language only)
 * - Accent normalization
 * - Confidence scoring for fuzzy matches
 * - Category exclusivity rules for certain modifier types
 */

// Modifier categories that only allow one selection per category
const EXCLUSIVE_MODIFIER_CATEGORIES = [
  Attribute.AttributeTitles.equipment, // "equipment-string"
  Attribute.AttributeTitles.rom, // "rom-string"
  Attribute.AttributeTitles.tempo, // "tempo-string"
  Attribute.AttributeTitles.stanceGrip, // "stanceGrip-string"
];

/**
 * Result object structure for search matches
 * @typedef {Object} ExerciseSearchResult
 * @property {string} exerciseId - ID of the matched exercise
 * @property {string} exerciseOriginalName - Original (key) name of the exercise
 * @property {string} exerciseName - Localized display name of the exercise
 * @property {string} exerciseType - Type/category of the exercise
 * @property {Array<ModifierMatch>} modifiers - Array of matched modifiers
 * @property {number} confidence - Confidence score (0-1) for the match quality
 */

/**
 * Modifier match object structure
 * @typedef {Object} ModifierMatch
 * @property {string} id - ID of the matched modifier
 * @property {string} originalName - Original (key) name of the modifier
 * @property {string} name - Localized display name of the modifier
 * @property {string} type - Category/title of the modifier
 * @property {string} subtype - Subcategory/subtitle of the modifier (if any)
 */

class ExerciseSearchService {
  constructor() {
    this.exerciseIndex = null;
    this.modifierIndex = null;
    this.lastLanguage = null;
  }

  /**
   * Get history blocks for a given block.
   * Prefers client.sortedBlocks when available (for client blocks),
   * otherwise uses coach.blockLibrary (for library blocks).
   * @param {Object} block - Block object
   * @returns {Array} Array of blocks to use for history, excluding the current block
   */
  getHistoryBlocks(block) {
    if (!block) return [];

    // Prefer client.sortedBlocks for client blocks
    if (block.client && Array.isArray(block.client.sortedBlocks)) {
      return block.client.sortedBlocks.filter((b) => b && b.id !== block.id);
    }

    // Fall back to coach.blockLibrary for library blocks
    if (block.coach && Array.isArray(block.coach.blockLibrary)) {
      return block.coach.blockLibrary.filter((b) => b && b.id !== block.id);
    }

    return [];
  }

  getSuggestedBaseExercisesFromHistory(
    block,
    context,
    suggestions,
    limit = 2,
    currentExercise = null
  ) {
    if (!Array.isArray(suggestions) || suggestions.length === 0) return [];

    const suggestedByOriginalName = new Map(
      suggestions
        .filter((s) => s && s.exerciseOriginalName)
        .map((s) => [s.exerciseOriginalName, s])
    );

    // Use the helper to get history blocks (supports both client and library blocks)
    const otherBlocks = this.getHistoryBlocks(block).slice(0, 4);

    if (otherBlocks.length === 0) {
      return suggestions.slice(0, Math.max(0, limit));
    }

    const counts = new Map();
    const bestWeek = new Map();

    const addSeen = (exercise, weight, week) => {
      const originalName = exercise?.originalName;
      if (!originalName) return;
      if (!suggestedByOriginalName.has(originalName)) return;
      if (
        currentExercise?.originalName &&
        originalName === currentExercise.originalName
      ) {
        return;
      }

      counts.set(originalName, (counts.get(originalName) || 0) + weight);
      const prevWeek = bestWeek.get(originalName) || 0;
      if (typeof week === "number" && week > prevWeek) {
        bestWeek.set(originalName, week);
      }
    };

    const processBlock = (targetBlock, recencyWeight) => {
      const allExercises = targetBlock?.protocolsAndExercises || [];

      const slots = new Set(
        allExercises
          .filter(
            (ex) =>
              ex &&
              ex.getProtocolBasicExercise === context &&
              !ex.isSecondaryProtocol &&
              ex.protocolSlot
          )
          .map((ex) => ex.protocolSlot)
      );

      if (slots.size === 0) return;

      const weeks = allExercises
        .filter(
          (ex) =>
            ex &&
            ex.getProtocolBasicExercise === context &&
            !ex.isSecondaryProtocol
        )
        .map((ex) => ex.getWeek || 0);

      const maxWeek = Math.max(0, ...weeks);
      if (maxWeek <= 0) return;

      for (let week = maxWeek; week >= 1; week--) {
        for (const slot of slots) {
          const result = targetBlock.getExercisesForSlotAndWeek(slot, week);
          if (result?.primary) {
            addSeen(result.primary, recencyWeight, week);
          }
          if (result?.secondary) {
            addSeen(result.secondary, recencyWeight, week);
          }
        }
      }
    };

    // Most recent blocks get higher weight.
    otherBlocks.forEach((b, idx) => {
      const weight = Math.max(1, 4 - idx);
      processBlock(b, weight);
    });

    const rankedOriginalNames = Array.from(counts.keys()).sort((a, b) => {
      const ca = counts.get(a) || 0;
      const cb = counts.get(b) || 0;
      if (cb !== ca) return cb - ca;
      const wa = bestWeek.get(a) || 0;
      const wb = bestWeek.get(b) || 0;
      return wb - wa;
    });

    const ranked = rankedOriginalNames
      .map((name) => suggestedByOriginalName.get(name))
      .filter(Boolean);

    // If we couldn't find any history overlap, fall back to the generic suggestions order.
    if (ranked.length === 0) {
      return suggestions.slice(0, Math.max(0, limit));
    }

    return ranked.slice(0, Math.max(0, limit));
  }

  /**
   * Initialize or refresh the search index
   * Call this when language changes or when predefined data updates
   * @param {Array} predefinedExercises - Array from customPredefinedExercises
   * @param {Array} predefinedAttributes - Array from customPredefinedAttributes
   */
  buildIndex(predefinedExercises, predefinedAttributes) {
    const currentLanguage = i18n.language;

    const getStableOriginalName = (obj) =>
      obj?.originalName ||
      obj?.original_name ||
      obj?.original ||
      obj?.name ||
      "";

    // Build exercise index
    this.exerciseIndex = predefinedExercises.map((exercise) => {
      const localizedName = i18n.t(exercise.name);
      const normalizedName = this.normalizeString(localizedName);
      const words = this.tokenize(normalizedName);

      return {
        id: exercise.id,
        originalName: getStableOriginalName(exercise),
        name: exercise.name, // The key for translation
        localizedName: localizedName,
        normalizedName: normalizedName,
        words: words,
        type: exercise.type,
        wordCount: words.length,
      };
    });

    // Sort exercises by word count descending (longer names first for better matching)
    this.exerciseIndex.sort((a, b) => b.wordCount - a.wordCount);

    // Build modifier index
    this.modifierIndex = predefinedAttributes.map((attr) => {
      const localizedName = i18n.t(attr.name);
      const normalizedName = this.normalizeString(localizedName);
      const words = this.tokenize(normalizedName);

      return {
        id: attr.id,
        originalName: getStableOriginalName(attr),
        name: attr.name, // The key for translation
        localizedName: localizedName,
        normalizedName: normalizedName,
        words: words,
        type: attr.title, // Category (equipment, tempo, etc.)
        subtype: attr.subtitle || null,
        wordCount: words.length,
      };
    });

    // Sort modifiers by word count descending
    this.modifierIndex.sort((a, b) => b.wordCount - a.wordCount);

    this.lastLanguage = currentLanguage;
  }

  /**
   * Check if index needs rebuilding (language changed)
   */
  needsRebuild() {
    return this.lastLanguage !== i18n.language || !this.exerciseIndex;
  }

  /**
   * Normalize a string for comparison
   * - Lowercase
   * - Remove accents/diacritics
   * - Trim whitespace
   * @param {string} str - Input string
   * @returns {string} Normalized string
   */
  normalizeString(str) {
    if (!str) return "";
    return str
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // Remove diacritics
      .replace(/[^a-z0-9]+/g, " ") // Strip punctuation/symbols consistently
      .replace(/\s+/g, " ")
      .trim();
  }

  /**
   * Tokenize a string into words
   * @param {string} str - Normalized input string
   * @returns {Array<string>} Array of words
   */
  tokenize(str) {
    if (!str) return [];
    return str.split(/\s+/).filter((word) => word.length > 0);
  }

  /**
   * Check if all words from a target appear in the search words (in any order)
   * Returns a match score based on:
   * - Exact word matches (highest score)
   * - Partial word matches (lower score)
   * - Word sequence/order bonus
   * @param {Array<string>} searchWords - Words from search query
   * @param {Array<string>} targetWords - Words from exercise/modifier name
   * @returns {Object} { matches: boolean, score: number, matchedIndices: Set, sequenceBonus: number }
   */
  calculateWordMatch(searchWords, targetWords) {
    if (targetWords.length === 0) {
      return {
        matches: false,
        score: 0,
        matchedIndices: new Set(),
        exactMatchedIndices: new Set(),
        sequenceBonus: 0,
      };
    }

    let totalScore = 0;
    let matchedTargetWords = 0;
    const matchedSearchIndices = new Set();
    const exactMatchedSearchIndices = new Set();
    const targetToSearchMap = new Map(); // Maps target word index to search word index

    // Helps choose the best occurrence when the same token appears multiple times
    // in the query (e.g., "baja" in "pausa baja ... barra baja").
    let lastSelectedSearchIndex = null;

    const isDigits = (w) => /^[0-9]+$/.test(w);
    const isSingleLetterAlpha = (w) => /^[a-z]$/.test(w);
    const isNegationToken = (w) => w === "not" || w === "no" || w === "sin";

    const tokenCounts = new Map();
    for (const w of searchWords) {
      tokenCounts.set(w, (tokenCounts.get(w) || 0) + 1);
    }

    const targetOrder = Array.from({ length: targetWords.length }, (_, i) => i);
    if (targetWords.length > 1) {
      const firstWord = targetWords[0];
      const firstCount = tokenCounts.get(firstWord) || 0;

      // If the phrase begins with a repeated token (or a negation token), match the
      // rest of the phrase first so we can bind the first token to the correct
      // occurrence (e.g., "sin ..." in "sin X sin Y").
      if (firstCount > 1 || isNegationToken(firstWord)) {
        targetOrder.push(targetOrder.shift());
      }
    }

    for (const targetIdx of targetOrder) {
      const targetWord = targetWords[targetIdx];
      let bestMatchScore = 0;
      let bestMatchIndex = -1;
      let bestMatchIsExact = false;

      // Tie-breaker: lower is better
      let bestTieKey = Infinity;

      const getTieKey = (candidateIndex) => {
        // If the phrase starts with a token that appears multiple times in the query
        // (e.g. "low" in "... low 2ct pause ... low pin"), prefer the occurrence that
        // sits immediately before the other matched words for this phrase.
        if (targetIdx === 0 && matchedSearchIndices.size > 0) {
          const count = tokenCounts.get(targetWord) || 0;
          const shouldPreferBefore = count > 1 || isNegationToken(targetWord);
          if (!shouldPreferBefore) {
            // fall through
          } else {
            const anchorIndex = Math.min(...Array.from(matchedSearchIndices));
            if (candidateIndex < anchorIndex) {
              return anchorIndex - candidateIndex; // closer before anchor
            }
            return 100 + (candidateIndex - anchorIndex); // after anchor is worse (but allowed)
          }
        }

        if (lastSelectedSearchIndex !== null) {
          // Prefer continuing forward in the query when there are ties.
          // This fixes repeated-token cases like "... agarre medio" where "medio"
          // appears twice; we want the "medio" after "agarre".
          if (candidateIndex >= lastSelectedSearchIndex) {
            return candidateIndex - lastSelectedSearchIndex;
          }
          return 1000 + (lastSelectedSearchIndex - candidateIndex);
        }

        return candidateIndex;
      };

      for (let i = 0; i < searchWords.length; i++) {
        if (matchedSearchIndices.has(i)) continue; // Already used this search word

        const searchWord = searchWords[i];

        // When the query contains multiple tokens, treat 1-letter tokens as strict.
        // Example: "barra z" should not match "barra zercher" via prefix.
        const queryHasMultipleWords = searchWords.length > 1;
        const isStrictSingleLetter =
          queryHasMultipleWords && isSingleLetterAlpha(searchWord);

        // Numeric tokens must match exactly (prevents "300" matching "30")
        if (isDigits(searchWord) || isDigits(targetWord)) {
          if (
            isDigits(searchWord) &&
            isDigits(targetWord) &&
            searchWord === targetWord
          ) {
            const candidateScore = 1.0;
            const tieKey = getTieKey(i);
            if (
              candidateScore > bestMatchScore ||
              (candidateScore === bestMatchScore && tieKey < bestTieKey)
            ) {
              bestMatchScore = candidateScore;
              bestMatchIndex = i;
              bestMatchIsExact = true;
              bestTieKey = tieKey;
            }
          }
          continue;
        }

        // Exact match
        if (searchWord === targetWord) {
          const candidateScore = 1.0;
          const tieKey = getTieKey(i);
          if (
            candidateScore > bestMatchScore ||
            (candidateScore === bestMatchScore && tieKey < bestTieKey)
          ) {
            bestMatchScore = candidateScore;
            bestMatchIndex = i;
            bestMatchIsExact = true;
            bestTieKey = tieKey;
          }
        }
        // Target word starts with search word (partial)
        else if (
          !isStrictSingleLetter &&
          targetWord.startsWith(searchWord) &&
          searchWord.length >= 1
        ) {
          const partialScore = searchWord.length / targetWord.length;
          const tieKey = getTieKey(i);
          if (
            partialScore > bestMatchScore ||
            (partialScore === bestMatchScore && tieKey < bestTieKey)
          ) {
            bestMatchScore = partialScore;
            bestMatchIndex = i;
            bestMatchIsExact = false;
            bestTieKey = tieKey;
          }
        }
        // Search word starts with target word (user typed more)
        else if (
          !isStrictSingleLetter &&
          searchWord.startsWith(targetWord) &&
          targetWord.length >= 1
        ) {
          const partialScore = (targetWord.length / searchWord.length) * 0.8;
          const tieKey = getTieKey(i);
          if (
            partialScore > bestMatchScore ||
            (partialScore === bestMatchScore && tieKey < bestTieKey)
          ) {
            bestMatchScore = partialScore;
            bestMatchIndex = i;
            bestMatchIsExact = false;
            bestTieKey = tieKey;
          }
        }
      }

      if (bestMatchScore > 0) {
        totalScore += bestMatchScore;
        matchedTargetWords++;
        if (bestMatchIndex >= 0) {
          matchedSearchIndices.add(bestMatchIndex);
          if (bestMatchIsExact) {
            exactMatchedSearchIndices.add(bestMatchIndex);
          }
          targetToSearchMap.set(targetIdx, bestMatchIndex);
          lastSelectedSearchIndex = bestMatchIndex;
        }
      }
    }

    // For early predictions, allow partial matches if at least one word is matched
    // Score is higher when more words match
    const hasMatch = matchedTargetWords > 0;
    const averageScore = hasMatch ? totalScore / targetWords.length : 0;

    // Boost score when all target words are matched (complete match)
    const completionBonus = matchedTargetWords === targetWords.length ? 0.2 : 0;

    // Calculate sequence bonus: reward when matched words appear in similar order
    let sequenceBonus = 0;
    if (targetToSearchMap.size > 1) {
      const mappedPairs = Array.from(targetToSearchMap.entries()).sort(
        (a, b) => a[0] - b[0]
      );
      let sequentialPairs = 0;
      let totalPairs = mappedPairs.length - 1;

      for (let i = 0; i < mappedPairs.length - 1; i++) {
        const [targetIdx1, searchIdx1] = mappedPairs[i];
        const [targetIdx2, searchIdx2] = mappedPairs[i + 1];

        // Check if the search indices are in the same relative order as target indices
        // and are relatively close together
        if (searchIdx2 > searchIdx1) {
          const targetGap = targetIdx2 - targetIdx1;
          const searchGap = searchIdx2 - searchIdx1;

          // Reward when gaps are similar (words appear in similar sequence)
          if (targetGap === searchGap) {
            sequentialPairs += 1.0; // Perfect sequence
          } else if (Math.abs(targetGap - searchGap) <= 1) {
            sequentialPairs += 0.5; // Close sequence
          }
        }
      }

      sequenceBonus = totalPairs > 0 ? (sequentialPairs / totalPairs) * 0.3 : 0;
    }

    return {
      matches: hasMatch,
      score: averageScore + completionBonus,
      matchedIndices: matchedSearchIndices,
      exactMatchedIndices: exactMatchedSearchIndices,
      sequenceBonus: sequenceBonus,
    };
  }

  /**
   * Find exercises that match the search query
   * @param {Array<string>} searchWords - Tokenized search words
   * @returns {Array} Array of exercise matches with scores and remaining words
   */
  findExerciseMatches(searchWords) {
    const matches = [];

    for (const exercise of this.exerciseIndex) {
      const matchResult = this.calculateWordMatch(searchWords, exercise.words);

      if (matchResult.matches) {
        // Calculate remaining words after removing matched ones
        const remainingWords = searchWords.filter(
          (_, i) => !matchResult.matchedIndices.has(i)
        );

        // Base score from word matching
        let adjustedScore = matchResult.score;

        // Add sequence bonus (words in similar order)
        adjustedScore += matchResult.sequenceBonus;

        // CRITICAL: Strong penalty for unmatched search words
        // Matching all words (even in different order) is better than partial match
        const matchedWordRatio =
          (searchWords.length - remainingWords.length) / searchWords.length;
        if (matchedWordRatio < 1.0) {
          // Heavy penalty for not matching all search words
          const unmatchedPenalty = (1.0 - matchedWordRatio) * 0.5;
          adjustedScore -= unmatchedPenalty;
        } else {
          // Bonus for matching ALL search words
          adjustedScore += 0.3;
        }

        // CRITICAL: Penalty for exercises with EXTRA words not in search query
        // "3ct Pause Low Bar Squat" (5 words) should rank higher than "Barbell Low 3ct Pause Squat" (5 words)
        // when searching "low bar squat 3ct pause" (5 words)
        const extraWordsInExercise =
          exercise.wordCount - matchResult.matchedIndices.size;
        if (extraWordsInExercise > 0) {
          // Penalize exercises that have words the user didn't search for
          const extraWordPenalty =
            (extraWordsInExercise / exercise.wordCount) * 0.4;
          adjustedScore -= extraWordPenalty;
        }

        // Bonus for exact word count match (exercise has same number of words as search)
        if (
          exercise.wordCount === searchWords.length &&
          remainingWords.length === 0
        ) {
          adjustedScore += 0.2; // Perfect match bonus
        }

        matches.push({
          exercise: exercise,
          score: adjustedScore,
          remainingWords: remainingWords,
          matchedSearchWordCount: matchResult.matchedIndices.size,
          exactMatchedSearchWordCount: matchResult.exactMatchedIndices.size,
        });
      }
    }

    // Sort by score descending
    matches.sort((a, b) => b.score - a.score);

    return matches;
  }

  /**
   * Find modifiers that match the remaining search words
   * Enforces category exclusivity rules
   * Uses combinatorial search to find the best set of modifiers
   * @param {Array<string>} remainingWords - Words not matched by exercise
   * @returns {Array<ModifierMatch>} Array of matched modifiers
   */
  findModifierMatches(remainingWords) {
    if (remainingWords.length === 0) {
      return [];
    }

    // Find ALL potential modifier matches with their matched word indices
    const allPotentialMatches = [];

    for (const modifier of this.modifierIndex) {
      const matchResult = this.calculateWordMatch(
        remainingWords,
        modifier.words
      );

      if (matchResult.matches) {
        // Allow partial modifier matches (same behavior as exercises).
        // Example: typing "pin" should surface "Low Pin" / "High Pin".
        allPotentialMatches.push({
          modifier: modifier,
          score: matchResult.score + (matchResult.sequenceBonus || 0),
          matchedIndices: matchResult.matchedIndices,
          wordCount: modifier.wordCount,
          matchedSearchWordCount: matchResult.matchedIndices.size,
          exactMatchedSearchWordCount: matchResult.exactMatchedIndices.size,
        });
      }
    }

    if (allPotentialMatches.length === 0) {
      return [];
    }

    // Now find the best non-overlapping combination of modifiers
    // that respects category exclusivity and maximizes coverage + score
    const bestCombination = this.findBestModifierCombination(
      allPotentialMatches,
      remainingWords.length
    );

    return bestCombination.map((m) => ({
      modifier: m.modifier,
      score: m.score,
      matchedSearchWordCount: m.matchedSearchWordCount || 0,
      exactMatchedSearchWordCount: m.exactMatchedSearchWordCount || 0,
      wordCount: m.wordCount || m.modifier?.wordCount || 0,
    }));
  }

  /**
   * Find the best combination of modifiers that don't overlap
   * and respect category exclusivity rules
   * @param {Array} potentialMatches - All potential modifier matches
   * @param {number} totalWords - Total number of remaining words
   * @returns {Array} Best combination of modifiers
   */
  findBestModifierCombination(potentialMatches, totalWords) {
    // Group matches by the words they use (to detect overlaps)
    // We'll use a recursive approach with memoization for small inputs
    // or a greedy approach that considers all non-overlapping options

    let bestCombination = [];
    let bestScore = -Infinity;
    let bestCoverage = 0;

    // For performance, limit combinations to explore
    // IMPORTANT: Sort first so the best candidates are explored.
    const sortedMatches = [...potentialMatches].sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      if ((b.matchedSearchWordCount || 0) !== (a.matchedSearchWordCount || 0)) {
        return (
          (b.matchedSearchWordCount || 0) - (a.matchedSearchWordCount || 0)
        );
      }
      return (b.wordCount || 0) - (a.wordCount || 0);
    });

    const maxMatches = Math.min(sortedMatches.length, 15);
    const limitedMatches = sortedMatches.slice(0, maxMatches);

    // Use recursive backtracking to find best combination
    const explore = (index, currentCombo, usedIndices, usedCategories) => {
      // Calculate current score
      const coverage = usedIndices.size;
      const totalScore = currentCombo.reduce((sum, m) => sum + m.score, 0);
      const extraUnmatchedModifierWords = currentCombo.reduce((sum, m) => {
        const wc = m.wordCount || m.modifier?.wordCount || 0;
        const matched = m.matchedSearchWordCount || 0;
        return sum + Math.max(0, wc - matched);
      }, 0);
      const exactMatched = currentCombo.reduce(
        (sum, m) => sum + (m.exactMatchedSearchWordCount || 0),
        0
      );

      // Prioritize query-word coverage first, but strongly discourage modifiers that
      // introduce extra words not present in the query (partial modifier picks).
      const normalizedScore =
        coverage * 10 +
        totalScore +
        exactMatched * 0.15 -
        extraUnmatchedModifierWords * 1.25;

      if (
        normalizedScore > bestScore ||
        (normalizedScore === bestScore && coverage > bestCoverage)
      ) {
        bestScore = normalizedScore;
        bestCoverage = coverage;
        bestCombination = [...currentCombo];
      }

      // Early termination if we've used all words
      if (usedIndices.size === totalWords) {
        return;
      }

      // Try adding each remaining modifier
      for (let i = index; i < limitedMatches.length; i++) {
        const match = limitedMatches[i];

        // Check for overlap with already used indices
        let hasOverlap = false;
        for (const idx of match.matchedIndices) {
          if (usedIndices.has(idx)) {
            hasOverlap = true;
            break;
          }
        }
        if (hasOverlap) continue;

        // Check category exclusivity
        if (
          EXCLUSIVE_MODIFIER_CATEGORIES.includes(match.modifier.type) &&
          usedCategories.has(match.modifier.type)
        ) {
          continue;
        }

        // Add this modifier and recurse
        const newUsedIndices = new Set(usedIndices);
        for (const idx of match.matchedIndices) {
          newUsedIndices.add(idx);
        }

        const newUsedCategories = new Set(usedCategories);
        if (EXCLUSIVE_MODIFIER_CATEGORIES.includes(match.modifier.type)) {
          newUsedCategories.add(match.modifier.type);
        }

        currentCombo.push(match);
        explore(i + 1, currentCombo, newUsedIndices, newUsedCategories);
        currentCombo.pop();
      }
    };

    explore(0, [], new Set(), new Set());

    return bestCombination;
  }

  /**
   * Get top modifier candidates for suggestions.
   * This is used to generate multiple modifier-variants even when the best
   * combination is only one modifier.
   */
  getModifierCandidates(remainingWords, limit = 8) {
    if (!remainingWords || remainingWords.length === 0) return [];

    const candidates = [];
    for (const modifier of this.modifierIndex) {
      const matchResult = this.calculateWordMatch(
        remainingWords,
        modifier.words
      );
      if (!matchResult.matches) continue;

      candidates.push({
        modifier,
        score: matchResult.score + (matchResult.sequenceBonus || 0),
        matchedIndices: matchResult.matchedIndices,
        wordCount: modifier.wordCount,
        matchedSearchWordCount: matchResult.matchedIndices.size,
        exactMatchedSearchWordCount: matchResult.exactMatchedIndices.size,
      });
    }

    candidates.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      if (b.matchedSearchWordCount !== a.matchedSearchWordCount) {
        return b.matchedSearchWordCount - a.matchedSearchWordCount;
      }
      return b.wordCount - a.wordCount;
    });

    return candidates.slice(0, Math.max(0, limit));
  }

  canCombineModifierCandidates(candidates) {
    const usedIndices = new Set();
    const usedCategories = new Set();

    for (const candidate of candidates) {
      // overlap
      for (const idx of candidate.matchedIndices || []) {
        if (usedIndices.has(idx)) return false;
      }

      // exclusivity
      const type = candidate?.modifier?.type;
      if (
        type &&
        EXCLUSIVE_MODIFIER_CATEGORIES.includes(type) &&
        usedCategories.has(type)
      ) {
        return false;
      }

      for (const idx of candidate.matchedIndices || []) {
        usedIndices.add(idx);
      }
      if (type && EXCLUSIVE_MODIFIER_CATEGORIES.includes(type)) {
        usedCategories.add(type);
      }
    }

    return true;
  }

  /**
   * Get suggestions based on context when query is empty
   * @param {string} context - Context identifier (e.g., 'squat')
   * @param {Array} predefinedExercises - Array from customPredefinedExercises
   * @param {Object} currentExercise - Optional current exercise to exclude from suggestions
   * @returns {Array} Filtered exercises formatted as search results
   */
  getSuggestions(context, predefinedExercises, currentExercise = null) {
    if (!context || !predefinedExercises) return [];

    let filteredExercises = [];

    switch (context) {
      case "squat":
        filteredExercises = this.getSquatSuggestions(predefinedExercises);
        break;
      case "benchPress":
        filteredExercises = this.getBenchPressSuggestions(predefinedExercises);
        break;
      case "deadlift":
        filteredExercises = this.getDeadliftSuggestions(predefinedExercises);
        break;
      default:
        return [];
    }

    // // Filter out current exercise if provided
    // if (currentExercise) {
    //   filteredExercises = filteredExercises.filter(
    //     (ex) => ex.originalName !== currentExercise.originalName
    //   );
    // }

    // Map to search result format
    return filteredExercises.slice(0, 10).map((ex) => ({
      exerciseId: ex.id,
      exerciseOriginalName: ex.originalName,
      exerciseName: i18n.t(ex.name),
      exerciseType: ex.type,
      modifiers: [],
      confidence: 1.0,
    }));
  }

  /**
   * Get fallback suggestions for workout mode (first N per exercise type).
   * Keeps current library ordering per type (order asc, fallback to original array order).
   * @param {Array} predefinedExercises - Array from customPredefinedExercises
   * @param {number} limitPerType - Max items per type
   * @returns {Array} Array of exercises formatted as search results
   */
  getWorkoutFallbackSuggestions(predefinedExercises, limitPerType = 2) {
    if (!Array.isArray(predefinedExercises) || predefinedExercises.length === 0) {
      return [];
    }

    const byType = new Map();
    predefinedExercises.forEach((exercise, index) => {
      if (!exercise) return;
      const type = exercise.type || "unknown";
      if (!byType.has(type)) {
        byType.set(type, []);
      }
      byType.get(type).push({ exercise, index });
    });

    const results = [];
    for (const entries of byType.values()) {
      entries.sort((a, b) => {
        const orderA = typeof a.exercise.order === "number" ? a.exercise.order : null;
        const orderB = typeof b.exercise.order === "number" ? b.exercise.order : null;
        if (orderA !== null && orderB !== null) return orderA - orderB;
        if (orderA !== null) return -1;
        if (orderB !== null) return 1;
        return a.index - b.index;
      });

      entries.slice(0, Math.max(0, limitPerType)).forEach(({ exercise }) => {
        results.push({
          exerciseId: exercise.id,
          exerciseOriginalName: exercise.originalName,
          exerciseName: i18n.t(exercise.name),
          exerciseType: exercise.type,
          modifiers: [],
          confidence: 1.0,
        });
      });
    }

    return results;
  }

  /**
   * Filter exercises for Squat suggestions
   * @param {Array} exercises
   * @returns {Array} Filtered exercises
   */
  getSquatSuggestions(exercises) {
    const positiveTokens = new Set([
      "squat",
      "sentadilla",
      "sentadillas",
      "esquat",
      "esquats",
    ]);
    const hardExcludes = new Set([
      "split",
      "bulgarian",
      "bulgara",
      "goblet",
      "hack",
      "belt",
      "sissy",
      "pistol",
      "lunge",
      "zancada",
      "step",
      "jump",
      "salto",
      "plyo",
    ]);
    const abbreviationTokens = new Set(["sq", "sqt"]);
    const contextKeywords = new Set([
      "comp",
      "competition",
      "competicion",
      "pause",
      "pausa",
      "tempo",
      "low",
      "high",
      "hb",
      "lb",
      "front",
      "back",
      "bar",
      "ssb",
      "safety",
      "box",
      "pin",
      "band",
      "chains",
      "raw",
      "beltless",
    ]);

    return exercises.filter((exercise) => {
      const name = i18n.t(exercise.name);
      // Normalize: lowercase, remove accents, replace non-alphanum with space, collapse spaces
      const normalized = name
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]/g, " ")
        .replace(/\s+/g, " ")
        .trim();

      const tokens = normalized.split(" ").filter((t) => t.length > 0);

      // 3. Hard excludes (check first for efficiency)
      if (tokens.some((t) => hardExcludes.has(t))) return false;

      // 2. Positive tokens
      if (tokens.some((t) => positiveTokens.has(t))) return true;

      // 4. Abbreviation support
      if (tokens.some((t) => abbreviationTokens.has(t))) {
        // Context check
        if (tokens.length <= 2) return true;
        if (tokens.some((t) => contextKeywords.has(t))) return true;
      }

      return false;
    });
  }

  /**
   * Filter exercises for Bench Press suggestions
   * @param {Array} exercises
   * @returns {Array} Filtered exercises
   */
  getBenchPressSuggestions(exercises) {
    const hardExcludeTokens = new Set([
      "row",
      "remo",
      "seal",
      "dip",
      "dips",
      "fondos",
      "fondo",
    ]);

    return exercises.filter((exercise) => {
      const originalName = exercise.originalName || "";
      const name = i18n.t(exercise.name);

      // Hard exclude: original name contains + or &
      if (originalName.includes("+") || originalName.includes("&")) {
        return false;
      }

      // Normalize: insert spaces in camelCase, lowercase, remove accents, replace non-alphanum, collapse spaces
      const normalized = name
        .replace(/([a-z])([A-Z])/g, "$1 $2") // camelCase → spaces
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]/g, " ")
        .replace(/\s+/g, " ")
        .trim();

      const tokens = normalized.split(" ").filter((t) => t.length > 0);

      // Hard excludes (tokens)
      if (tokens.some((t) => hardExcludeTokens.has(t))) return false;

      // Special case: bench + pull but not press (bench pull)
      const hasBench = tokens.some((t) => t.includes("bench"));
      const hasPull = tokens.some((t) => t.includes("pull"));
      const hasPress = tokens.some(
        (t) => t.includes("press") || t.includes("prensa")
      );
      if (hasBench && hasPull && !hasPress) return false;

      // Positive match
      const hasBenchToken = tokens.some((t) => t.includes("bench"));
      const hasBancaToken = tokens.some((t) => t.includes("banca"));
      const hasAbbreviation = tokens.some((t) => t === "bp" || t === "pb");

      return hasBenchToken || hasBancaToken || hasAbbreviation;
    });
  }

  /**
   * Filter exercises for Deadlift suggestions
   * @param {Array} exercises
   * @returns {Array} Filtered exercises
   */
  getDeadliftSuggestions(exercises) {
    const hardExcludeTokens = new Set([
      "rdl",
      "romanian",
      "rumanian",
      "rumano",
      "rumana",
      "stiff",
      "stiffleg",
      "rigida",
      "rigidas",
      "rigido",
      "rigidos",
      "suitcase",
      "trap",
      "hex",
      "trapbar",
      "hexbar",
      "hexabar",
      "snatch",
      "clean",
      "jerk",
      "block",
      "blocks",
      "bloque",
      "bloques",
      "single",
      "one",
      "unilateral",
      "leg",
      "pierna",
      "kettlebell",
      "kb",
      "swing",
      "jump",
      "plyo",
      "salto",
      "seated",
      "sentado",
    ]);

    const abbreviationContextKeywords = new Set([
      "sumo",
      "conventional",
      "conv",
      "convencional",
      "comp",
      "competition",
      "competicion",
      "pause",
      "pausa",
      "tempo",
      "deficit",
      "raw",
      "beltless",
      "reset",
      "pin",
      "pins",
    ]);

    return exercises.filter((exercise) => {
      const originalName = exercise.originalName || "";
      const name = i18n.t(exercise.name);

      // Hard exclude: original name contains + or &
      if (originalName.includes("+") || originalName.includes("&")) {
        return false;
      }

      // Normalize: split camelCase, lowercase, remove accents, replace non-alphanum, collapse spaces
      const normalized = name
        .replace(/([a-z])([A-Z])/g, "$1 $2") // camelCase → spaces
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]/g, " ")
        .replace(/\s+/g, " ")
        .trim();

      const tokens = normalized.split(" ").filter((t) => t.length > 0);

      // Hard excludes (tokens)
      if (tokens.some((t) => hardExcludeTokens.has(t))) return false;

      // Positive signals
      let matchedByAbbreviation = false;

      // 1. Direct deadlift token or (dead + lift)
      const hasDeadlift = tokens.some((t) => t === "deadlift");
      const hasDead = tokens.some((t) => t === "dead");
      const hasLift = tokens.some((t) => t === "lift");
      if (hasDeadlift || (hasDead && hasLift)) return true;

      // 2. Spanish/Catalan: peso + muerto/morto OR pes + mort
      const hasPeso = tokens.some((t) => t === "peso");
      const hasPes = tokens.some((t) => t === "pes");
      const hasMuerto = tokens.some((t) => t === "muerto" || t === "morto");
      const hasMort = tokens.some((t) => t === "mort");
      if ((hasPeso && hasMuerto) || (hasPes && hasMort)) return true;

      // 3. Abbreviation: dl or pm
      const hasDL = tokens.some((t) => t === "dl");
      const hasPM = tokens.some((t) => t === "pm");
      if (hasDL || hasPM) {
        matchedByAbbreviation = true;

        // Abbreviation rule: accept only if token count ≤ 3 OR has context keywords
        if (tokens.length <= 3) return true;
        if (tokens.some((t) => abbreviationContextKeywords.has(t))) return true;
      }

      return false;
    });
  }

  /**
   * Get recently used exercises from a block with prioritization
   * @param {Object} block - Block object with protocolsAndExercises
   * @param {string} context - Context identifier (e.g., 'squat', 'benchPress', 'deadlift')
   * @param {string} protocolSlot - Current protocol slot (e.g., "primarySquat-string")
   * @param {number} currentWeek - Current week number
   * @param {Object} currentExercise - Optional current exercise to exclude from results
   * @returns {Array} Array of unique exercises matching the context, prioritized by slot and recency
   */
  getRecentlyUsedExercises(
    block,
    context,
    protocolSlot = null,
    currentWeek = null,
    currentExercise = null
  ) {
    if (!block || !context) return [];

    // Determine the basic exercise type from context
    const basicExercise = context; // "squat", "benchPress", or "deadlift";

    const exercisesWithPriority = [];
    const seen = new Set();

    const buildExerciseKey = (ex) => {
      if (!ex) return "";
      const attributes = Array.isArray(ex.attributes) ? ex.attributes : [];
      const attributeKey = attributes
        .map((attr) => attr.name)
        .sort()
        .join("|");
      return `${ex.originalName}|${attributeKey}`;
    };

    // Always show the currently selected exercise first when provided
    if (currentExercise) {
      const currentKey = buildExerciseKey(currentExercise);
      if (!seen.has(currentKey)) {
        seen.add(currentKey);
        exercisesWithPriority.push({
          exercise: currentExercise,
          priority: 0,
          week: currentWeek || Number.MAX_SAFE_INTEGER,
        });
      }
    }

    // Helper to add exercise with deduplication
    const addExercise = (ex, priority, week) => {
      if (!ex) return;
      const key = buildExerciseKey(ex);
      if (!seen.has(key)) {
        seen.add(key);
        exercisesWithPriority.push({
          exercise: ex,
          priority: priority,
          week: week,
        });
      }
    };

    // Helper to process exercises from a block with a given base priority
    const processBlockExercises = (targetBlock, basePriority) => {
      const allExercises = targetBlock.protocolsAndExercises || [];

      // Priority 1 (or adjusted): Current slot, previous weeks (most recent first)
      if (protocolSlot && currentWeek && targetBlock === block) {
        for (let week = currentWeek - 1; week >= 1; week--) {
          const result = targetBlock.getExercisesForSlotAndWeek(
            protocolSlot,
            week
          );
          if (result && result.primary) {
            addExercise(result.primary, basePriority, week);
          }
          if (result && result.secondary) {
            addExercise(result.secondary, basePriority, week);
          }
        }

        // Still Priority 1: also include following weeks for the current slot,
        // but after previous-week picks (so they don't outrank true recency).
        // Use a slightly worse priority than basePriority, but still better than
        // the "other slots" priority (basePriority + 1).
        const slotMaxWeek = Math.max(
          ...allExercises
            .filter(
              (ex) =>
                ex &&
                ex.getProtocolBasicExercise === basicExercise &&
                !ex.isSecondaryProtocol &&
                ex.protocolSlot === protocolSlot
            )
            .map((ex) => ex.getWeek || 0),
          currentWeek
        );

        for (let week = currentWeek + 1; week <= slotMaxWeek; week++) {
          const result = targetBlock.getExercisesForSlotAndWeek(
            protocolSlot,
            week
          );
          if (result && result.primary) {
            addExercise(result.primary, basePriority + 0.5, week);
          }
          if (result && result.secondary) {
            addExercise(result.secondary, basePriority + 0.5, week);
          }
        }
      }

      // Priority 2 (or adjusted): Other slots of the same basic exercise
      // First, collect all unique protocolSlots for this basic exercise (excluding current slot if same block)
      const otherSlots = new Set(
        allExercises
          .filter(
            (ex) =>
              ex.getProtocolBasicExercise === basicExercise &&
              !ex.isSecondaryProtocol &&
              (targetBlock !== block || ex.protocolSlot !== protocolSlot) &&
              ex.protocolSlot
          )
          .map((ex) => ex.protocolSlot)
      );

      // Find the maximum week across all exercises of this basic exercise
      const maxWeek = Math.max(
        ...allExercises
          .filter(
            (ex) =>
              ex.getProtocolBasicExercise === basicExercise &&
              !ex.isSecondaryProtocol
          )
          .map((ex) => ex.getWeek || 0),
        targetBlock === block && currentWeek ? currentWeek : 0
      );

      // Iterate from highest week down, checking each slot
      for (let week = maxWeek; week >= 1; week--) {
        for (const slot of otherSlots) {
          const result = targetBlock.getExercisesForSlotAndWeek(slot, week);
          if (result && result.primary) {
            addExercise(result.primary, basePriority + 1, week);
          }
          if (result && result.secondary) {
            addExercise(result.secondary, basePriority + 1, week);
          }
        }
      }
    };

    // Process current block with priority 1-2
    processBlockExercises(block, 1);

    // Process last 4 blocks from history (supports both client and library blocks)
    const otherBlocks = this.getHistoryBlocks(block).slice(0, 4);

    // Process each block with increasing priority (3, 5, 7, 9)
    otherBlocks.forEach((otherBlock, index) => {
      const blockPriority = 3 + index * 2; // 3, 5, 7, 9
      processBlockExercises(otherBlock, blockPriority);
    });

    // Sort by priority (lower is better), then by week (higher is better)
    exercisesWithPriority.sort((a, b) => {
      if (a.priority !== b.priority) {
        return a.priority - b.priority;
      }
      return b.week - a.week;
    });

    // Return just the exercises, limit to 10
    return exercisesWithPriority.slice(0, 10).map((item) => item.exercise);
  }

  /**
   * Main search function
   * @param {string} query - User's search input
   * @param {Array} predefinedExercises - Array from customPredefinedExercises
   * @param {Array} predefinedAttributes - Array from customPredefinedAttributes
   * @returns {Array<ExerciseSearchResult>} Ranked list of search results
   */
  search(query, predefinedExercises, predefinedAttributes) {
    // Rebuild index if needed
    if (this.needsRebuild()) {
      this.buildIndex(predefinedExercises, predefinedAttributes);
    }

    // Normalize and tokenize query
    const normalizedQuery = this.normalizeString(query);
    const searchWords = this.tokenize(normalizedQuery);

    if (searchWords.length === 0) {
      return [];
    }

    // Find all matching exercises
    const exerciseMatches = this.findExerciseMatches(searchWords);

    // If no exercise matches, but we DO recognize at least one modifier token,
    // optionally provide 1-2 context-aware base-exercise predictions.
    // This is intentionally narrow so it doesn't affect the excellent existing logic.
    if (exerciseMatches.length === 0) {
      const modifierCandidates = this.getModifierCandidates(searchWords, 6);
      if (modifierCandidates.length === 0) {
        return [];
      }

      // Backwards-compatible: only run this fallback if caller provided context + block.
      const options = arguments.length >= 4 ? arguments[3] : null;
      const context = options?.context;
      const block = options?.block;
      const currentExercise = options?.currentExercise || null;

      if (!context || !block) {
        return [];
      }

      const topModifier = modifierCandidates[0];
      const hasMeaningfulModifierSignal =
        (topModifier?.matchedSearchWordCount || 0) >= 1 &&
        (topModifier?.score || 0) >= 0.25;

      if (!hasMeaningfulModifierSignal) {
        return [];
      }

      const bestModifiers = this.findModifierMatches(searchWords);
      if (!Array.isArray(bestModifiers) || bestModifiers.length === 0) {
        return [];
      }

      const baseSuggestions = this.getSuggestions(
        context,
        predefinedExercises,
        currentExercise
      );

      if (!Array.isArray(baseSuggestions) || baseSuggestions.length === 0) {
        return [];
      }

      const predictedBases = this.getSuggestedBaseExercisesFromHistory(
        block,
        context,
        baseSuggestions,
        2,
        currentExercise
      );

      const matchedModifierWords = bestModifiers.reduce(
        (sum, m) => sum + (m.matchedSearchWordCount || 0),
        0
      );
      const modifierCoverage = Math.min(
        1,
        matchedModifierWords / Math.max(1, searchWords.length)
      );

      return predictedBases.map((base, idx) => {
        const confidence = Math.min(
          0.85,
          0.35 + modifierCoverage * 0.45 + (idx === 0 ? 0.05 : 0)
        );

        return {
          exerciseId: base.exerciseId,
          exerciseOriginalName: base.exerciseOriginalName,
          exerciseName: base.exerciseName,
          exerciseType: base.exerciseType,
          modifiers: bestModifiers.map((m) => ({
            id: m.modifier.id,
            originalName: m.modifier.originalName,
            name: m.modifier.localizedName,
            type: m.modifier.type,
            subtype: m.modifier.subtype,
          })),
          confidence,
        };
      });
    }

    // Group exercise matches by exercise (to limit to top 3 exercises)
    // Then for each top exercise, generate multiple modifier combinations.
    // IMPORTANT: choose the top exercises by best full combo (exercise + modifiers),
    // not by exercise-only score. This prevents long exercise names that merely
    // contain modifier words from beating the intended "base exercise + modifiers".
    const precomputedBestModifiersByExerciseKey = new Map();
    const getExerciseKey = (exerciseMatch) =>
      exerciseMatch?.exercise?.originalName ||
      exerciseMatch?.exercise?.id ||
      "";

    // Build results with modifier variations for each top exercise
    const results = [];

    const calculateComboStats = (exerciseMatch, combo) => {
      const exerciseQueryMatchedCount =
        exerciseMatch.matchedSearchWordCount ??
        searchWords.length - exerciseMatch.remainingWords.length;
      const exerciseExactMatchedCount =
        exerciseMatch.exactMatchedSearchWordCount ?? 0;
      const modifierQueryMatchedCount = (combo || []).reduce(
        (sum, m) => sum + (m.matchedSearchWordCount || 0),
        0
      );
      const modifierExactMatchedCount = (combo || []).reduce(
        (sum, m) => sum + (m.exactMatchedSearchWordCount || 0),
        0
      );
      const matchedQueryWordsCount =
        exerciseQueryMatchedCount + modifierQueryMatchedCount;
      const exactMatchedQueryWordsCount =
        exerciseExactMatchedCount + modifierExactMatchedCount;

      const exerciseExtraWordsCount = Math.max(
        0,
        (exerciseMatch.exercise?.wordCount || 0) - exerciseQueryMatchedCount
      );
      const modifierExtraWordsCount = (combo || []).reduce((sum, m) => {
        const wc = m.wordCount || m.modifier?.wordCount || 0;
        const matched = m.matchedSearchWordCount || 0;
        return sum + Math.max(0, wc - matched);
      }, 0);

      const extraWordsCount = exerciseExtraWordsCount + modifierExtraWordsCount;
      const strictExactWordsMatch =
        matchedQueryWordsCount === searchWords.length &&
        exactMatchedQueryWordsCount === searchWords.length &&
        extraWordsCount === 0;

      return {
        matchedQueryWordsCount,
        exactMatchedQueryWordsCount,
        extraWordsCount,
        strictExactWordsMatch,
        coverageRatio: Math.min(1, matchedQueryWordsCount / searchWords.length),
      };
    };

    const exerciseCandidates = [];
    const maxDistinctExercisesToConsider = 15;
    const maxExerciseMatchesToScan = Math.min(exerciseMatches.length, 60);
    const seenExerciseKeys = new Set();

    for (let i = 0; i < maxExerciseMatchesToScan; i += 1) {
      const exerciseMatch = exerciseMatches[i];
      const key = getExerciseKey(exerciseMatch);
      if (!key || seenExerciseKeys.has(key)) continue;
      seenExerciseKeys.add(key);

      const bestModifiers = this.findModifierMatches(
        exerciseMatch.remainingWords
      );
      precomputedBestModifiersByExerciseKey.set(key, bestModifiers);
      const stats = calculateComboStats(exerciseMatch, bestModifiers);

      exerciseCandidates.push({
        exerciseMatch,
        stats,
      });

      if (exerciseCandidates.length >= maxDistinctExercisesToConsider) break;
    }

    exerciseCandidates.sort((a, b) => {
      if (!!b.stats.strictExactWordsMatch !== !!a.stats.strictExactWordsMatch) {
        return (
          (b.stats.strictExactWordsMatch ? 1 : 0) -
          (a.stats.strictExactWordsMatch ? 1 : 0)
        );
      }
      if (b.stats.matchedQueryWordsCount !== a.stats.matchedQueryWordsCount) {
        return b.stats.matchedQueryWordsCount - a.stats.matchedQueryWordsCount;
      }
      if (a.stats.extraWordsCount !== b.stats.extraWordsCount) {
        return a.stats.extraWordsCount - b.stats.extraWordsCount;
      }
      return b.exerciseMatch.score - a.exerciseMatch.score;
    });

    const topExercises = exerciseCandidates
      .slice(0, 3)
      .map((c) => c.exerciseMatch);

    for (const exerciseMatch of topExercises) {
      const key = getExerciseKey(exerciseMatch);
      const modifierMatches =
        (key && precomputedBestModifiersByExerciseKey.get(key)) ||
        this.findModifierMatches(exerciseMatch.remainingWords);

      // Also gather top modifier candidates to provide richer suggestions
      // when the user typed only a partial modifier (e.g. "pin" -> "Low Pin", "High Pin").
      const modifierCandidates = this.getModifierCandidates(
        exerciseMatch.remainingWords,
        8
      );

      const baseStats = calculateComboStats(exerciseMatch, modifierMatches);

      // Calculate base confidence with emphasis on full coverage
      let baseConfidence = 0;

      // Major component: word coverage (60% weight) - increased to prioritize full matches
      baseConfidence += baseStats.coverageRatio * 0.6;

      // Exercise match quality (20% weight) - reduced to show fewer different exercises
      baseConfidence += exerciseMatch.score * 0.2;

      // Modifier match quality (20% weight)
      if (modifierMatches.length > 0) {
        const avgModifierScore =
          modifierMatches.reduce((sum, m) => sum + m.score, 0) /
          modifierMatches.length;
        baseConfidence += avgModifierScore * 0.2;
      } else if (exerciseMatch.remainingWords.length === 0) {
        // No remaining words and no modifiers = perfect exercise-only match
        baseConfidence += 0.2;
      }

      // CRITICAL BONUS: Perfect coverage (all search words accounted for)
      if (baseStats.matchedQueryWordsCount === searchWords.length) {
        baseConfidence += 0.4; // Bigger bonus for matching ALL typed words
      }

      // Penalty for introducing extra words not in the query.
      // This prevents partial-modifier suggestions (e.g. "baja" -> "pausa baja 2ct")
      // from outranking an exact match like "sentadilla barra baja".
      if (baseStats.extraWordsCount > 0) {
        const extraPenalty = Math.min(
          0.55,
          (baseStats.extraWordsCount /
            Math.max(1, (exerciseMatch.exercise?.wordCount || 0) + 3)) *
            0.55
        );
        baseConfidence -= extraPenalty;
      }

      if (baseStats.strictExactWordsMatch) {
        baseConfidence = 1;
      }

      baseConfidence = Math.min(1, Math.max(0, baseConfidence));

      // Generate result with matched modifiers
      results.push({
        exerciseId: exerciseMatch.exercise.id,
        exerciseOriginalName: exerciseMatch.exercise.originalName,
        exerciseName: exerciseMatch.exercise.localizedName,
        exerciseType: exerciseMatch.exercise.type,
        modifiers: modifierMatches.map((m) => ({
          id: m.modifier.id,
          originalName: m.modifier.originalName,
          name: m.modifier.localizedName,
          type: m.modifier.type,
          subtype: m.modifier.subtype,
        })),
        confidence: baseConfidence,
        _matchedExerciseWords: exerciseMatch.exercise.words.length,
        _remainingWords: exerciseMatch.remainingWords,
        _matchedQueryWordsCount: baseStats.matchedQueryWordsCount,
        _extraWordsCount: baseStats.extraWordsCount,
        _strictExactWordsMatch: baseStats.strictExactWordsMatch,
      });

      // If there are remaining words, also suggest this exercise without modifiers
      // (in case the remaining words are not valid modifiers)
      if (
        exerciseMatch.remainingWords.length > 0 &&
        modifierMatches.length === 0
      ) {
        results.push({
          exerciseId: exerciseMatch.exercise.id,
          exerciseOriginalName: exerciseMatch.exercise.originalName,
          exerciseName: exerciseMatch.exercise.localizedName,
          exerciseType: exerciseMatch.exercise.type,
          modifiers: [],
          confidence: baseConfidence * 0.9, // Slightly lower confidence
          _matchedExerciseWords: exerciseMatch.exercise.words.length,
          _remainingWords: exerciseMatch.remainingWords,
          _matchedQueryWordsCount: baseStats.matchedQueryWordsCount,
          _extraWordsCount: baseStats.extraWordsCount,
          _strictExactWordsMatch: baseStats.strictExactWordsMatch,
        });
      }

      // Generate alternative modifier combinations to provide more options
      if (modifierCandidates.length > 0) {
        const alternativeCandidateCombos = [];

        // Option 1: Exercise without any modifiers
        alternativeCandidateCombos.push([]);

        // Option 2: Individual modifiers (top candidates)
        for (let i = 0; i < Math.min(modifierCandidates.length, 5); i++) {
          alternativeCandidateCombos.push([modifierCandidates[i]]);
        }

        // Option 3: Simple non-overlapping pairs from the top candidates
        for (let i = 0; i < Math.min(modifierCandidates.length, 4); i++) {
          for (let j = i + 1; j < Math.min(modifierCandidates.length, 4); j++) {
            const pair = [modifierCandidates[i], modifierCandidates[j]];
            if (this.canCombineModifierCandidates(pair)) {
              alternativeCandidateCombos.push(pair);
            }
          }
        }

        for (const candidateCombo of alternativeCandidateCombos) {
          // Calculate confidence based on how much we deviate from the best combo
          const comboStats = calculateComboStats(exerciseMatch, candidateCombo);
          let comboConfidence = 0;

          // Start from the same components as base, but recompute coverage/extra-penalty
          comboConfidence += comboStats.coverageRatio * 0.6;
          comboConfidence += exerciseMatch.score * 0.2;

          if (candidateCombo.length > 0) {
            const avgCandidateScore =
              candidateCombo.reduce((sum, m) => sum + (m.score || 0), 0) /
              candidateCombo.length;
            comboConfidence += avgCandidateScore * 0.2;
          } else if (exerciseMatch.remainingWords.length === 0) {
            comboConfidence += 0.2;
          }

          if (comboStats.matchedQueryWordsCount === searchWords.length) {
            comboConfidence += 0.4;
          }

          if (comboStats.extraWordsCount > 0) {
            const extraPenalty = Math.min(
              0.55,
              (comboStats.extraWordsCount /
                Math.max(1, (exerciseMatch.exercise?.wordCount || 0) + 3)) *
                0.55
            );
            comboConfidence -= extraPenalty;
          }

          if (comboStats.strictExactWordsMatch) {
            comboConfidence = 1;
          }

          if (candidateCombo.length === 0) {
            comboConfidence *= 0.85;
          } else if (
            modifierMatches.length > 0 &&
            candidateCombo.length < modifierMatches.length
          ) {
            comboConfidence *= 0.92;
          }

          comboConfidence = Math.min(1, Math.max(0, comboConfidence));

          results.push({
            exerciseId: exerciseMatch.exercise.id,
            exerciseOriginalName: exerciseMatch.exercise.originalName,
            exerciseName: exerciseMatch.exercise.localizedName,
            exerciseType: exerciseMatch.exercise.type,
            modifiers: candidateCombo.map((c) => ({
              id: c.modifier.id,
              originalName: c.modifier.originalName,
              name: c.modifier.localizedName,
              type: c.modifier.type,
              subtype: c.modifier.subtype,
            })),
            confidence: comboConfidence,
            _matchedExerciseWords: exerciseMatch.exercise.words.length,
            _remainingWords: exerciseMatch.remainingWords,
            _matchedQueryWordsCount: comboStats.matchedQueryWordsCount,
            _extraWordsCount: comboStats.extraWordsCount,
            _strictExactWordsMatch: comboStats.strictExactWordsMatch,
          });
        }
      }
    }

    // Sort with a hard guarantee:
    // - Exact word set (covers all query words, no extra words) must always win.
    // - Then prefer more query-word coverage and fewer extra words.
    results.sort((a, b) => {
      if (!!b._strictExactWordsMatch !== !!a._strictExactWordsMatch) {
        return (
          (b._strictExactWordsMatch ? 1 : 0) -
          (a._strictExactWordsMatch ? 1 : 0)
        );
      }
      const aMatched = a._matchedQueryWordsCount || 0;
      const bMatched = b._matchedQueryWordsCount || 0;
      if (bMatched !== aMatched) return bMatched - aMatched;

      const aExtra = a._extraWordsCount || 0;
      const bExtra = b._extraWordsCount || 0;
      if (aExtra !== bExtra) return aExtra - bExtra;

      return b.confidence - a.confidence;
    });

    // Remove duplicates (same exercise with same modifiers)
    const seen = new Set();
    const uniqueResults = results.filter((result) => {
      const key = `${result.exerciseOriginalName}-${result.modifiers
        .map((m) => m.originalName)
        .sort()
        .join(",")}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });

    return uniqueResults.slice(0, 10);
  }

  // #region /* WORKOUT SUGGESTIONS (for ExerciseCard) */

  /**
   * Build a deterministic exercise key for workout exercises
   * Reuses the same pattern as buildExerciseKey in getRecentlyUsedExercises
   * @param {Object} ex - Exercise object
   * @returns {string} Exercise key
   */
  buildExerciseKeyForWorkout(ex) {
    if (!ex) return "";
    const attributes = Array.isArray(ex.attributes) ? ex.attributes : [];
    const attributeKey = attributes
      .map((attr) => attr.name)
      .sort()
      .join("|");
    return `${ex.originalName}|${attributeKey}`;
  }

  /**
   * Build a session key map for a given week
   * Maps workout.order to sequential session keys (0, 1, 2, 3...)
   * @param {Object} block - Block object
   * @param {number} week - Week number
   * @returns {Map<number, number>} Map from order to sessionKey
   */
  buildSessionKeyMap(block, week) {
    const weekWorkouts = (block.workouts || [])
      .filter((w) => w.week === week)
      .sort((a, b) => a.order - b.order);

    const map = new Map();
    weekWorkouts.forEach((w, idx) => map.set(w.order, idx));
    return map;
  }

  /**
   * Get the previous block (most recent block that is not current)
   * Supports both client blocks and library blocks.
   * @param {Object} block - Current block
   * @returns {Object|null} Previous block or null
   */
  getPreviousBlock(block) {
    const historyBlocks = this.getHistoryBlocks(block);
    return historyBlocks.length > 0 ? historyBlocks[0] : null;
  }

  /**
   * Convert an exercise to the search result format
   * @param {Object} ex - Exercise object
   * @returns {Object} Result in search result format
   */
  exerciseToResult(ex) {
    return {
      exerciseId: ex.id,
      exerciseOriginalName: ex.originalName,
      exerciseName: i18n.t(ex.originalName),
      exerciseType: ex.type || "",
      modifiers: (ex.attributes || []).map((attr) => ({
        id: attr.id,
        originalName: attr.name,
        name: i18n.t(attr.name),
        type: attr.title,
        subtype: attr.subtitle || null,
      })),
      confidence: 1.0,
    };
  }

  /**
   * Collect candidate exercises from a block (single pass)
   * @param {Object} blk - Block to process
   * @param {Map} candidateMap - Map to accumulate candidates
   * @param {Set} excludeKeys - Keys to exclude (exercises in current workout)
   * @param {string|null} currentExerciseKey - Key of current exercise (to keep even if in excludeKeys)
   * @param {number} currentSessionKey - Session key of current workout
   * @param {number} currentWeek - Week of current workout
   * @param {number} currentPos - Position of current exercise in workout
   * @param {boolean} isCurrentBlock - Whether this is the current block
   * @param {Map} sessionKeyMap - Session key map for current week
   */
  collectCandidatesFromBlock(
    blk,
    candidateMap,
    excludeKeys,
    currentExerciseKey,
    currentSessionKey,
    currentWeek,
    currentPos,
    isCurrentBlock,
    sessionKeyMap
  ) {
    const blockWeight = isCurrentBlock ? 0 : 10; // Current block preferred

    for (const workout of blk.workouts || []) {
      // Skip: only process workouts with exercises
      const exercises = workout.exercises;
      if (!Array.isArray(exercises) || exercises.length === 0) continue;

      // Build session key for this workout's week if needed
      let workoutSessionKey;
      if (workout.week === currentWeek && isCurrentBlock) {
        workoutSessionKey = sessionKeyMap.get(workout.order) ?? 0;
      } else {
        // For other weeks/blocks, use order directly
        workoutSessionKey = workout.order;
      }

      const isSameSession = workoutSessionKey === currentSessionKey;
      const isSameWeek = workout.week === currentWeek;
      const weekDistance = Math.abs(workout.week - currentWeek);

      // Determine priority bucket
      let priority;
      if (isSameSession && !isSameWeek) {
        priority = 100 + blockWeight + weekDistance; // Best: same session, different week
      } else if (!isSameWeek) {
        priority = 200 + blockWeight + weekDistance; // Good: different week
      } else {
        priority = 300 + blockWeight; // Lowest: same week, different session
      }

      // Collect exercises
      for (let posIdx = 0; posIdx < exercises.length; posIdx++) {
        const ex = exercises[posIdx];
        if (!ex) continue;

        // Filter: only non-protocol exercises
        if (ex.protocolSlot && ex.protocolSlot.trim() !== "") continue;

        const key = this.buildExerciseKeyForWorkout(ex);

        // Skip if in current workout (but keep if it's the current exercise being edited)
        if (excludeKeys.has(key) && key !== currentExerciseKey) continue;

        // Add or update candidate
        if (!candidateMap.has(key)) {
          candidateMap.set(key, {
            key,
            exercise: ex,
            occurrences: [],
            frequency: 0,
          });
        }

        const candidate = candidateMap.get(key);
        candidate.frequency++;
        candidate.occurrences.push({ priority, pos: posIdx });
      }
    }
  }

  /**
   * Score candidates based on priority and position similarity
   * @param {Map} candidateMap - Map of candidates
   * @param {number} currentPos - Current exercise position
   * @returns {Array} Sorted array of scored candidates
   */
  scoreCandidates(candidateMap, currentPos) {
    const candidates = [];

    for (const [key, data] of candidateMap) {
      // Best priority from occurrences (lower is better)
      const bestPriority = Math.min(...data.occurrences.map((o) => o.priority));

      // Average position across all occurrences
      const avgPos =
        data.occurrences.reduce((sum, o) => sum + o.pos, 0) /
        data.occurrences.length;

      // Position distance penalty (0-10 range, lower is better)
      const posDistance = Math.abs(avgPos - currentPos);
      const positionPenalty = Math.min(posDistance * 2, 20); // Cap at 20

      // Final score: priority + position penalty - frequency bonus
      const frequencyBonus = Math.min(data.frequency * 0.5, 5); // Cap bonus at 5
      const score = bestPriority + positionPenalty - frequencyBonus;

      candidates.push({
        key,
        exercise: data.exercise,
        score,
        bestPriority,
        avgPos,
        frequency: data.frequency,
      });
    }

    // Sort by score (lower is better)
    candidates.sort((a, b) => a.score - b.score);

    return candidates;
  }

  /**
   * Get workout-based suggestions for ExerciseCard
   * Returns exercises from current and previous block ranked by:
   * 1. Session matching (same session key, different week)
   * 2. Week proximity
   * 3. Position similarity in workout
   *
   * @param {Object} block - Current block
   * @param {Object} currentWorkout - The workout being edited
   * @param {Object} currentExercise - The exercise being edited (shown first)
   * @param {number} exerciseIndex - Current exercise's 0-based position in workout
   * @returns {Array} Array of exercise suggestions in search result format
   */
  getWorkoutSuggestions(
    block,
    currentWorkout,
    currentExercise = null,
    exerciseIndex = 0
  ) {
    if (!block || !currentWorkout) return [];

    const currentWeek = currentWorkout.week;
    const currentOrder = currentWorkout.order;
    const currentPos = exerciseIndex;

    // Build session key map for current week (O(n) where n = workouts in week)
    const sessionKeyMap = this.buildSessionKeyMap(block, currentWeek);
    const currentSessionKey = sessionKeyMap.get(currentOrder) ?? 0;

    // Exercises already in current workout (to exclude)
    const currentWorkoutKeys = new Set(
      (currentWorkout.exercises || [])
        .filter((ex) => ex && ex.id !== currentExercise?.id)
        .map((ex) => this.buildExerciseKeyForWorkout(ex))
    );

    // Current exercise key (to show first)
    const currentExerciseKey = currentExercise
      ? this.buildExerciseKeyForWorkout(currentExercise)
      : null;

    // Single-pass collection: candidateMap tracks per-exerciseKey data
    // Structure: Map<exerciseKey, { exercise, occurrences: Array<{priority, pos}>, frequency }>
    const candidateMap = new Map();

    // Process blocks (current first, then previous)
    const blocksToProcess = [{ blk: block, isCurrentBlock: true }];
    const prevBlock = this.getPreviousBlock(block);
    if (prevBlock) {
      blocksToProcess.push({ blk: prevBlock, isCurrentBlock: false });
    }

    for (const { blk, isCurrentBlock } of blocksToProcess) {
      this.collectCandidatesFromBlock(
        blk,
        candidateMap,
        currentWorkoutKeys,
        currentExerciseKey,
        currentSessionKey,
        currentWeek,
        currentPos,
        isCurrentBlock,
        sessionKeyMap
      );
    }

    // Score and sort candidates
    const scoredCandidates = this.scoreCandidates(candidateMap, currentPos);

    // Current exercise always first
    if (currentExercise && currentExerciseKey) {
      const currentResult = this.exerciseToResult(currentExercise);
      const filtered = scoredCandidates
        .filter((c) => c.key !== currentExerciseKey)
        .slice(0, 9);
      return [
        currentResult,
        ...filtered.map((c) => this.exerciseToResult(c.exercise)),
      ];
    }

    return scoredCandidates
      .slice(0, 10)
      .map((c) => this.exerciseToResult(c.exercise));
  }

  // #endregion

  // #region /* WORKOUT LIBRARY SEARCH */

  /**
   * Check if query is a prefix sequence match for target
   * e.g., "leg d" matches "leg day" but "leg x" does not match "leg day"
   * @param {string} query - Normalized search query
   * @param {string} target - Normalized workout name
   * @returns {boolean} Whether query matches target
   */
  isPrefixSequenceMatch(query, target) {
    if (!query || !target) return false;
    // Check if target starts with query or contains query
    return target.startsWith(query) || target.includes(query);
  }

  /**
   * Calculate match score for a workout name
   * @param {string} query - Normalized search query
   * @param {string} target - Normalized workout name
   * @returns {number} Score between 0 and 1
   */
  calculateWorkoutMatchScore(query, target) {
    if (!query || !target) return 0;

    // Exact match = highest score
    if (query === target) return 1.0;

    // Target starts with query = high score
    if (target.startsWith(query)) {
      return 0.9 + (query.length / target.length) * 0.1;
    }

    // Query appears elsewhere in target = lower score
    if (target.includes(query)) {
      return 0.7 + (query.length / target.length) * 0.1;
    }

    return 0;
  }

  /**
   * Search the workout library for matching workout names
   * @param {string} query - Search query
   * @param {Array} workoutLibrary - Array of workout objects from coach.workoutLibrary
   * @param {number} limit - Maximum number of results (default 3)
   * @returns {Array} Array of workout results with type: "workout"
   */
  searchWorkoutLibrary(query, workoutLibrary, limit = 3) {
    if (!query || !workoutLibrary || workoutLibrary.length === 0) return [];

    const normalizedQuery = this.normalizeString(query);
    if (!normalizedQuery) return [];

    const matches = [];

    // Single pass through workout library (O(n) where n = workouts in library)
    for (const workout of workoutLibrary) {
      if (!workout?.name) continue;

      const normalizedName = this.normalizeString(workout.name);

      // Check if query matches workout name as prefix sequence
      const isMatch = this.isPrefixSequenceMatch(
        normalizedQuery,
        normalizedName
      );

      if (isMatch) {
        // Calculate match score (higher = better)
        const score = this.calculateWorkoutMatchScore(
          normalizedQuery,
          normalizedName
        );

        matches.push({
          type: "workout",
          workout: workout, // Full workout object (includes exercises array)
          workoutId: workout.id,
          workoutName: workout.name,
          confidence: score,
        });
      }
    }

    // Sort by score descending, then by order descending (higher order prioritized)
    // Efficiency: typically only 0-5 matches, so sort is O(m log m) where m << n
    return matches
      .sort((a, b) => {
        // First sort by confidence (descending)
        if (b.confidence !== a.confidence) {
          return b.confidence - a.confidence;
        }
        // When confidence is equal, prioritize higher order (descending)
        const orderA = a.workout?.order ?? 0;
        const orderB = b.workout?.order ?? 0;
        return orderB - orderA;
      })
      .slice(0, limit);
  }

  /**
   * Merge exercise results with workout results, sorted by confidence
   * @param {Array} exerciseResults - Array of exercise search results
   * @param {Array} workoutResults - Array of workout search results
   * @returns {Array} Combined and sorted results
   */
  mergeResultsWithWorkouts(exerciseResults, workoutResults) {
    // Ensure exercise results have type: "exercise"
    const typedExerciseResults = exerciseResults.map((r) => ({
      ...r,
      type: r.type || "exercise",
    }));

    // Combine and sort by confidence descending
    const combined = [...typedExerciseResults, ...workoutResults];
    combined.sort((a, b) => (b.confidence || 0) - (a.confidence || 0));

    return combined;
  }

  // #endregion

  // #region /* PROTOCOL SLOT SUGGESTIONS */

  /**
   * Ordering for protocol slots: Squat -> Bench -> Deadlift, then Primary -> Quinary
   */
  static SLOT_ORDER = [
    // Squat
    "primarySquat-string",
    "secondarySquat-string",
    "tertiarySquat-string",
    "quaternarySquat-string",
    "quinarySquat-string",
    // Bench Press
    "primaryBenchPress-string",
    "secondaryBenchPress-string",
    "tertiaryBenchPress-string",
    "quaternaryBenchPress-string",
    "quinaryBenchPress-string",
    // Deadlift
    "primaryDeadlift-string",
    "secondaryDeadlift-string",
    "tertiaryDeadlift-string",
    "quaternaryDeadlift-string",
    "quinaryDeadlift-string",
  ];

  /**
   * Get available protocol slots for a given week
   * Returns slots that have not been used in any workout of that week
   * @param {Object} block - Block object with protocols and workouts
   * @param {number} currentWeek - The week number to check
   * @returns {Array} Array of slot results with type: "slot"
   */
  getAvailableSlots(block, currentWeek) {
    if (!block || !block.protocols || !currentWeek) return [];

    // Collect all taken protocol slots from workouts in this week
    // Check both exercises and exercisesToAdd (O(m*k) where m = workouts, k = exercises)
    const takenSlots = new Set();
    for (const workout of block.workouts || []) {
      if (workout.week !== currentWeek) continue;

      // Check exercises
      for (const ex of workout.exercises || []) {
        if (ex.protocolSlot) takenSlots.add(ex.protocolSlot);
      }
      // Check exercisesToAdd
      for (const ex of workout.exercisesToAdd || []) {
        if (ex.protocolSlot) takenSlots.add(ex.protocolSlot);
      }
    }

    // Group protocols by protocolSlot for this week (single pass O(n))
    // Map<protocolSlot, { primary: Exercise, secondary: Exercise | null }>
    const slotGroups = new Map();

    for (const protocol of block.protocols) {
      // Filter by week
      if (protocol.protocolWeek !== currentWeek) continue;

      const slot = protocol.protocolSlot;
      if (!slot) continue;

      // Skip if already used in a workout
      if (takenSlots.has(slot)) continue;

      // Get or create group
      if (!slotGroups.has(slot)) {
        slotGroups.set(slot, { primary: null, secondary: null });
      }

      const group = slotGroups.get(slot);
      if (protocol.isSecondaryProtocol) {
        group.secondary = protocol;
      } else {
        group.primary = protocol;
      }
    }

    // Convert to results array and filter out incomplete slots (must have primary)
    const results = [];
    for (const [slot, group] of slotGroups) {
      if (!group.primary) continue; // Skip if no primary exercise

      results.push({
        type: "slot",
        primaryExercise: group.primary,
        secondaryExercise: group.secondary,
        protocolSlot: slot,
        confidence: 1.0,
      });
    }

    // Sort by predefined order (O(n) with O(1) lookup per item)
    const orderMap = new Map(
      ExerciseSearchService.SLOT_ORDER.map((s, i) => [s, i])
    );
    results.sort((a, b) => {
      const orderA = orderMap.get(a.protocolSlot) ?? 999;
      const orderB = orderMap.get(b.protocolSlot) ?? 999;
      return orderA - orderB;
    });

    return results;
  }

  /**
   * Search available slots for matching exercise names
   * @param {string} query - Search query
   * @param {Object} block - Block object
   * @param {number} currentWeek - Current week number
   * @param {Function} t - Translation function
   * @param {string} language - Current language
   * @returns {Array} Array of matched slot results
   */
  searchSlotsForWorkout(query, block, currentWeek, t, language) {
    if (!query || !block || !currentWeek) return [];

    const availableSlots = this.getAvailableSlots(block, currentWeek);
    if (availableSlots.length === 0) return [];

    const normalizedQuery = this.normalizeString(query);
    if (!normalizedQuery) return [];

    const searchWords = this.tokenize(normalizedQuery);
    if (searchWords.length === 0) return [];

    const matches = [];

    for (const slot of availableSlots) {
      // Get exercise names for matching
      const primaryName = slot.primaryExercise?.getName?.(t, language) || "";
      const secondaryName =
        slot.secondaryExercise?.getName?.(t, language) || "";

      // Combine names for matching: "Primary Exercise + Secondary Exercise"
      const combinedName = secondaryName
        ? `${primaryName} ${secondaryName}`
        : primaryName;

      const normalizedName = this.normalizeString(combinedName);
      const targetWords = this.tokenize(normalizedName);

      // Use existing word match logic
      const matchResult = this.calculateWordMatch(searchWords, targetWords);

      if (matchResult.matches) {
        // Calculate confidence with slot boost
        let confidence = matchResult.score + matchResult.sequenceBonus;

        // Add slot boost (0.15) if base confidence is reasonable
        if (confidence >= 0.4) {
          confidence += 0.15;
        }

        // Filter out poor matches
        if (confidence < 0.4) continue;

        matches.push({
          ...slot,
          confidence: Math.min(1.0, confidence),
        });
      }
    }

    // Sort by confidence descending
    return matches.sort((a, b) => b.confidence - a.confidence);
  }

  /**
   * Merge exercise results with slot results, sorted by confidence
   * Slots are prioritized when confidence is similar (within 0.15 threshold)
   * @param {Array} exerciseResults - Array of exercise search results
   * @param {Array} slotResults - Array of slot search results
   * @returns {Array} Combined and sorted results
   */
  mergeResultsWithSlots(exerciseResults, slotResults) {
    // Ensure exercise results have type set
    const typedExerciseResults = exerciseResults.map((r) => ({
      ...r,
      type: r.type || "exercise",
    }));

    // Combine and sort with slot priority when confidence is similar
    const combined = [...typedExerciseResults, ...slotResults];
    combined.sort((a, b) => {
      const aConf = a.confidence || 0;
      const bConf = b.confidence || 0;
      const aIsSlot = a.type === "slot";
      const bIsSlot = b.type === "slot";

      // If one is a slot and the other isn't, and confidence is similar (within 0.15)
      // prioritize the slot
      if (aIsSlot && !bIsSlot) {
        const diff = bConf - aConf;
        // If slot confidence is within 0.15 of exercise, slot wins
        if (diff <= 0.15) {
          return -1; // slot (a) comes first
        }
      } else if (!aIsSlot && bIsSlot) {
        const diff = aConf - bConf;
        // If slot confidence is within 0.15 of exercise, slot wins
        if (diff <= 0.15) {
          return 1; // slot (b) comes first
        }
      }

      // Otherwise, sort by confidence descending
      return bConf - aConf;
    });

    return combined;
  }

  // #endregion

  /**
   * Invalidate the current index (call when predefined data changes)
   */
  invalidateIndex() {
    this.exerciseIndex = null;
    this.modifierIndex = null;
    this.lastLanguage = null;
  }
}

// Singleton instance
let instance = null;

/**
 * Get the singleton instance of ExerciseSearchService
 * @returns {ExerciseSearchService}
 */
export const getExerciseSearchService = () => {
  if (!instance) {
    instance = new ExerciseSearchService();
  }
  return instance;
};

export default ExerciseSearchService;
